import type { AttributeFilter } from '@/entities/dashboards';

/**
 * Целевое событие («цель») для матрицы и переходов.
 * По умолчанию (не задано) целью считается удержание (retention).
 * Варианты:
 * - `{ retention: true }` — целью явно является удержание;
 * - `{ attributePath, value }` — цель: диалоги, где атрибут принимает значение `value`;
 * - `{ attributePath, values }` — цель: диалоги, где атрибут принимает одно из `values`.
 */
export type StrategistTargetAttribute = {
  /** True — целью явно является удержание */
  retention?: boolean;
  /** Путь целевого атрибута (`conversation_attr_value.attribute_path`) */
  attributePath?: string;
  /** Одно целевое значение атрибута */
  value?: string;
  /** Несколько целевых значений атрибута */
  values?: string[];
};

/**
 * Персистируемые за пользователем настройки страницы «Структурные единицы»
 * (`uiSettings.strategistHierarchy`): сквозной фильтр, целевая метрика, категория
 * понятий и порог близости. Секция плоская — каждое поле пишется своим патчем.
 *
 * Все поля опциональны: набор мог быть записан прежним составом настроек, а пустое
 * значение хранится как отсутствующее.
 */
export type StrategistHierarchySettings = {
  /** Сквозные атрибутные фильтры выборки (формат общий с дашбордом качества) */
  filters?: AttributeFilter[];
  /** Начало периода в формате YYYY-MM-DD */
  dateFrom?: string;
  /** Конец периода в формате YYYY-MM-DD */
  dateTo?: string;
  /**
   * Целевая метрика. Отсутствие — законное сохранённое состояние «метрика не выбрана»
   * («Нет»), а не «настройка не задана»: при следующем входе метрика останется невыбранной.
   */
  target?: StrategistTargetAttribute;
  /** Категория понятий (роль концептов); применяется, только если есть среди доступных */
  category?: string;
  /** Порог близости; применяется, только если есть среди доступных порогов ответа */
  threshold?: number;
};

/**
 * Общая часть тела всех POST-запросов раздела «Стратег».
 * Формат `filters` совпадает с quality-dashboard (см. {@link AttributeFilter}).
 */
export type StrategistBaseRequest = {
  /** Сквозные атрибутные фильтры выборки */
  filters: AttributeFilter[];
  /** Начало периода в формате YYYY-MM-DD (по `strategist_dialog.date`) */
  dateFrom?: string;
  /** Конец периода в формате YYYY-MM-DD */
  dateTo?: string;
};

/** Тело POST /strategist-data/concepts. */
export type StrategistConceptsRequest = StrategistBaseRequest & {
  /** Фильтр по роли концепта (например, «возражение» / «аргумент») */
  role?: string;
};

/** Концепт с агрегатами по текущей выборке диалогов. */
export type StrategistConcept = {
  id: string;
  label: string | null;
  labelRaw: string | null;
  role: string | null;
  nodeType: string | null;
  speaker: string | null;
  /** Число диалогов выборки, где встречается концепт */
  nDialogs: number;
  /** Число узлов выборки с этим концептом */
  nNodes: number;
};

/** Ответ POST /strategist-data/concepts. */
export type StrategistConceptsResponse = {
  /** Число диалогов в выборке */
  totalDialogs: number;
  concepts: StrategistConcept[];
};

/** Тело POST /strategist-data/matrix. */
export type StrategistMatrixRequest = StrategistBaseRequest & {
  /** Целевое событие (по умолчанию — удержание) */
  targetAttribute?: StrategistTargetAttribute;
  /** Сколько строк/столбцов вернуть (топ по частоте) */
  top?: number;
  /** Порог близости для строк-возражений из набора `thresholds` */
  objectionThreshold?: number;
  /** Порог близости для столбцов-аргументов из набора `thresholds` */
  argumentThreshold?: number;
};

/** Строка (возражение) или столбец (аргумент) матрицы. */
export type StrategistMatrixAxisItem = {
  id: string;
  label: string | null;
  /** Частота: в скольких диалогах выборки участвует в парах */
  nDialogs: number;
};

/** Ячейка матрицы «возражение × аргумент». */
export type StrategistMatrixCell = {
  objectionId: string;
  argumentId: string;
  /** Диалоги, где возражение сменяется аргументом */
  nDialogs: number;
  /** Доля целевого исхода среди диалогов ячейки */
  targetRate: number | null;
};

/** Ответ POST /strategist-data/matrix. */
export type StrategistMatrixResponse = {
  totalDialogs: number;
  /** Доля цели по всей выборке */
  baselineRate: number | null;
  /** Возражения (role=возражение) */
  rows: StrategistMatrixAxisItem[];
  /** Аргументы (role=аргумент) */
  columns: StrategistMatrixAxisItem[];
  cells: StrategistMatrixCell[];
  /** Доступные пороги близости (шаги ползунков) */
  thresholds: number[];
  /** Применённый порог близости по возражениям (снапнут к ближайшему) или null */
  objectionThreshold: number | null;
  /** Применённый порог близости по аргументам (снапнут к ближайшему) или null */
  argumentThreshold: number | null;
};

/** Тело POST /strategist-data/transitions. */
export type StrategistTransitionsRequest = StrategistBaseRequest & {
  /** Целевое событие (по умолчанию — удержание) */
  targetAttribute?: StrategistTargetAttribute;
  /** Минимум диалогов на переход */
  minDialogs?: number;
  /** Порог близости по возражениям (узлы-источники, from) из набора `thresholds` */
  objectionThreshold?: number;
  /** Порог близости по аргументам (узлы-приёмники, to) из набора `thresholds` */
  argumentThreshold?: number;
};

/** Переход «возражение → следующий аргумент». */
export type StrategistTransition = {
  fromConceptId: string;
  /** label концепта-возражения */
  fromConcept: string | null;
  toConceptId: string;
  /** label концепта-аргумента */
  toConcept: string | null;
  /** Число диалогов с этим переходом */
  n: number;
  /** Доля целевого исхода среди диалогов перехода */
  targetRate: number | null;
  /** targetRate − baseline */
  lift: number | null;
};

/** Ответ POST /strategist-data/transitions. */
export type StrategistTransitionsResponse = {
  totalDialogs: number;
  /** Доля цели по всей выборке */
  baselineRate: number | null;
  transitions: StrategistTransition[];
  /** Доступные пороги близости (шаги ползунков) */
  thresholds: number[];
  /** Применённый порог близости по возражениям (снапнут к ближайшему) или null */
  objectionThreshold: number | null;
  /** Применённый порог близости по аргументам (снапнут к ближайшему) или null */
  argumentThreshold: number | null;
};

/**
 * Роль концептов-строк матрицы стандартов:
 * - `'возражение'` — возражения клиента;
 * - `'аргумент'` — обработка (аргументы) оператора.
 */
export type StrategistRowRole = 'возражение' | 'аргумент';

/** Коды ролей понятий, которыми их называет раздел: ими же задаётся роль строк матрицы. */
export const STRATEGIST_CONCEPT_ROLE = {
  objection: 'возражение',
  argument: 'аргумент',
  question: 'вопрос',
} as const;

/** Тело POST /strategist-data/standards-matrix. */
export type StrategistStandardsMatrixRequest = StrategistBaseRequest & {
  /** Целевое событие (по умолчанию — удержание) */
  targetAttribute?: StrategistTargetAttribute;
  /** Роль концептов-строк (по умолчанию — «возражение») */
  rowRole?: StrategistRowRole;
  /** Сколько строк вернуть (топ по частоте) */
  topRows?: number;
  /** Сколько столбцов-блоков вернуть (топ по частоте) */
  topCols?: number;
  /** Порог близости для строк (по роли строк) из набора `thresholds` */
  threshold?: number;
};

/** Строка матрицы стандартов — понятие из кластеров. */
export type StrategistStandardsRow = {
  id: string;
  label: string | null;
  /** Число диалогов выборки, где встречается понятие */
  nDialogs: number;
};

/** Столбец матрицы стандартов — блок стандарта (voicedScriptBlocks). */
export type StrategistStandardsColumn = {
  /** Код блока стандарта */
  code: string;
  label: string | null;
  /** Число диалогов выборки, где блок озвучен */
  nDialogs: number;
};

/** Ячейка матрицы «понятие × блок стандарта». */
export type StrategistStandardsCell = {
  /** Идентификатор понятия (строки) */
  conceptId: string;
  /** Код блока стандарта (столбца) */
  block: string;
  /** Диалоги, где понятие встретилось И блок озвучен */
  nDialogs: number;
  /** Доля целевого исхода среди диалогов ячейки */
  targetRate: number | null;
};

/** Ответ POST /strategist-data/standards-matrix. */
export type StrategistStandardsMatrixResponse = {
  totalDialogs: number;
  /** Доля цели по всей выборке */
  baselineRate: number | null;
  /** Понятия (строки) */
  rows: StrategistStandardsRow[];
  /** Блоки стандарта (столбцы) */
  columns: StrategistStandardsColumn[];
  cells: StrategistStandardsCell[];
  /** Доступные пороги близости (шаги ползунка) */
  thresholds: number[];
  /** Применённый порог близости строк (снапнут к ближайшему) или null */
  threshold: number | null;
};

/** Роль спикера реплики: `E` — оператор, `C` — клиент. */
export type StrategistSpeaker = 'E' | 'C';

/** Тело POST /strategist-data/concept — карточка одного понятия по выборке. */
export type StrategistConceptCardRequest = {
  /** Идентификатор понятия (концепта) */
  conceptId: string;
  /** Сквозные атрибутные фильтры выборки */
  filters?: AttributeFilter[];
  /** Начало периода в формате YYYY-MM-DD */
  dateFrom?: string;
  /** Конец периода в формате YYYY-MM-DD */
  dateTo?: string;
  /** Сколько примеров-реплик вернуть */
  limit?: number;
};

/** Пример реальной реплики понятия из конкретного диалога. */
export type StrategistConceptExample = {
  /** Идентификатор диалога (для ссылки `/dialogs/:cid`) */
  cid: number;
  /** Дословный анонимизированный текст реплики */
  text: string;
  /** Спикер реплики: оператор (`E`) или клиент (`C`) */
  speaker: StrategistSpeaker;
  /** Тип узла реплики */
  nodeType: string;
};

/** Ответ POST /strategist-data/concept — карточка понятия с примерами реплик. */
export type StrategistConceptCardResponse = {
  id: string;
  label: string | null;
  labelRaw: string | null;
  role: string | null;
  nodeType: string | null;
  speaker: string | null;
  /** Число узлов выборки с этим понятием */
  size: number;
  /** Число диалогов выборки, где встречается понятие */
  nDialogs: number;
  /** Доля целевого исхода среди диалогов понятия (0..1) или null */
  targetRate: number | null;
  /** Базовая доля цели по всей выборке (0..1) или null */
  baselineRate: number | null;
  /** Влияние понятия: targetRate − baseline (доля) или null */
  lift: number | null;
  /** Реальные реплики понятия по разным диалогам */
  examples: StrategistConceptExample[];
};

/** Тело POST /strategist-data/hierarchy. */
export type StrategistHierarchyRequest = StrategistBaseRequest & {
  /** Целевое событие (по умолчанию — удержание) */
  targetAttribute?: StrategistTargetAttribute;
  /** Категория (роль концептов): «возражение» / «аргумент» / «вопрос» */
  category?: string;
  /** Порог близости кластеризации из набора `thresholds` */
  threshold?: number;
};

/** Кластер понятий на выбранном пороге близости. */
export type StrategistHierarchyCluster = {
  /** Стабильный ключ кластера (медоид) */
  clusterKey: string | number;
  /** conceptId медоида кластера — для открытия карточки понятия */
  medoidConceptId: string;
  /** Подпись-медоид кластера */
  label: string;
  /** Число понятий, попавших в кластер */
  nConcepts: number;
  /** Число диалогов выборки, где встречается кластер */
  nDialogs: number;
  /** Доля целевого исхода среди диалогов кластера (0..1) или null */
  targetRate: number | null;
  /** Влияние кластера: targetRate − baselineRate (доля) или null */
  lift: number | null;
  /** Примеры формулировок из кластера */
  examples: string[];
};

/** Ответ POST /strategist-data/hierarchy. */
export type StrategistHierarchyResponse = {
  /** Число диалогов в выборке */
  totalDialogs: number;
  /** Базовая доля цели по всей выборке (0..1) или null */
  baselineRate: number | null;
  /** Текущая категория среза */
  category: string;
  /** Текущий порог близости */
  threshold: number;
  /** Доступные пороги близости (шаги ползунка) */
  thresholds: number[];
  /** Доступные категории (роли концептов) */
  categories: string[];
  /** Кластеры текущего среза */
  clusters: StrategistHierarchyCluster[];
};

/**
 * Тело POST /strategist-data/cell-dialogs — диалоги, стоящие за срезом
 * (парой возражение×аргумент, понятием×блоком стандарта либо одним понятием
 * строки иерархии), с влиянием среза. Срез адресуется хотя бы одним из:
 * `objectionId`, `argumentId`, `conceptId`, `block`.
 */
export type StrategistCellDialogsRequest = {
  /** Идентификатор понятия-возражения (строка матрицы) */
  objectionId?: string;
  /** Идентификатор понятия-аргумента (столбец матрицы возражение×аргумент) */
  argumentId?: string;
  /** Идентификатор понятия любой роли (строка иерархии) */
  conceptId?: string;
  /** Код блока стандарта (столбец матрицы стандартов) */
  block?: string;
  /** Порог схлопывания возражения (раскрытие медоида в кластер, как в матрице) */
  objectionThreshold?: number;
  /** Порог схлопывания аргумента (раскрытие медоида в кластер, как в матрице) */
  argumentThreshold?: number;
  /** Порог схлопывания понятия строки иерархии (раскрытие медоида в кластер) */
  conceptThreshold?: number;
  /** Сквозные атрибутные фильтры выборки */
  filters?: AttributeFilter[];
  /** Начало периода в формате YYYY-MM-DD */
  dateFrom?: string;
  /** Конец периода в формате YYYY-MM-DD */
  dateTo?: string;
  /** Целевое событие (по умолчанию — удержание) */
  targetAttribute?: StrategistTargetAttribute;
  /**
   * Срез — переход: аргумент обязан быть ближайшим после возражения, как в рейтинге
   * переходов. По умолчанию срез — ячейка матрицы: аргумент звучит где-либо позже
   * возражения. Действует при заданном `objectionId` и/или `argumentId`.
   */
  transition?: boolean;
  /** Сколько диалогов вернуть (по умолчанию 50) */
  limit?: number;
};

/**
 * Реальная реплика среза (узел-возражение или узел-аргумент) конкретного
 * диалога ячейки. Клик по реплике ведёт прямо к ней внутри диалога
 * (`/dialogs/:cid?phraseId=:phraseId`).
 */
export type StrategistCellReply = {
  /** Дословный анонимизированный текст реплики */
  text: string;
  /** Идентификатор фразы для перехода к реплике внутри диалога или null */
  phraseId: number | null;
  /** Спикер реплики: оператор (`E`) или клиент (`C`) */
  speaker: StrategistSpeaker;
  /** Роль реплики в срезе (например, «возражение» / «аргумент») */
  role: string;
};

/** Диалог, стоящий за ячейкой матрицы. */
export type StrategistCellDialog = {
  /** Идентификатор диалога (для ссылки `/dialogs/:cid`) */
  cid: number;
  /** Исход диалога (строка от бэка; трактуется как удержание / отказ) */
  outcome: string;
  /** Дата диалога в формате YYYY-MM-DD или null */
  date: string | null;
  /** Реальные реплики среза (узел-возражение и узел-аргумент) этого диалога */
  replies: StrategistCellReply[];
};

/** Ответ POST /strategist-data/cell-dialogs — влияние ячейки и её диалоги. */
export type StrategistCellDialogsResponse = {
  /** Число диалогов в ячейке */
  nDialogs: number;
  /** Доля целевого исхода среди диалогов ячейки (0..1) или null */
  targetRate: number | null;
  /** Базовая доля цели по всей выборке (0..1) или null */
  baselineRate: number | null;
  /** Влияние ячейки: targetRate − baseline (доля) или null */
  lift: number | null;
  /** Диалоги ячейки */
  dialogs: StrategistCellDialog[];
};
